import axiosInstance from '../auth/axiosInstance';

export interface Department {
  id: number;
  name: string;
  isActive: boolean;
  projectCount: number;
}

const normalizeDepartment = (row): Department | null => {
  if (!row || typeof row !== 'object') return null;
  const id = Number(row.id ?? row.Id);
  const name = String(row.name ?? row.Name ?? '').trim();
  if (!Number.isFinite(id) || id <= 0 || !name) return null;
  const isActiveRaw = row.isActive ?? row.IsActive;
  return {
    id,
    name,
    isActive: isActiveRaw === undefined || isActiveRaw === null ? true : Boolean(isActiveRaw),
    projectCount: Number(row.projectCount ?? row.ProjectCount ?? 0) || 0,
  };
};

const normalizeNames = (raw): string[] =>
  Array.isArray(raw) ? raw.map((name) => String(name ?? '').trim()).filter(Boolean) : [];

/** Pull the API message out of a 400 response so toasts show the real reason. */
const toError = (err, fallback: string) =>
  new Error(err?.response?.data?.message || err?.message || fallback);

export const getDepartments = async (search = '', activeOnly = true): Promise<Department[]> => {
  const params: Record<string, string | boolean> = { activeOnly };
  if (search) params.search = search;
  const { data: result } = await axiosInstance.get('/Department/GetDepartments', { params });

  if (result?.code && result.code >= 400) {
    throw new Error(result.message || 'Failed to load departments');
  }

  const list = Array.isArray(result?.data) ? result.data : [];
  return list.map(normalizeDepartment).filter(Boolean);
};

/** Active department names for dropdowns. */
export const getDepartmentNames = async (): Promise<string[]> => {
  const { data: result } = await axiosInstance.get('/Department/GetDepartmentNames');

  if (result?.code && result.code >= 400) {
    throw new Error(result.message || 'Failed to load departments');
  }

  return normalizeNames(result?.data);
};

export const addOrUpdateDepartment = async ({ id = 0, name, isActive = true }) => {
  try {
    const { data: result } = await axiosInstance.post('/Department/AddOrUpdateDepartment', {
      id,
      name: String(name || '').trim(),
      isActive,
    });

    if (result?.code && result.code >= 400) {
      throw new Error(result.message || 'Failed to save department');
    }

    return normalizeDepartment(result?.data) ?? result?.data;
  } catch (err) {
    throw toError(err, 'Failed to save department');
  }
};

export const deleteDepartment = async (id: number) => {
  const { data: result } = await axiosInstance.post(`/Department/DeleteDepartment/${id}`);

  const code = result?.code;
  const failed =
    code === 403 ||
    code === 'Forbidden' ||
    (typeof code === 'number' && code >= 400) ||
    result?.data === false;

  if (failed) {
    throw new Error(result?.message || 'Failed to delete department');
  }

  return result;
};
